import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import UnderlineMark from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import type { AnyExtension, Extensions } from "@tiptap/core";
import { Variable, DEFAULT_VARIABLE_REGEX } from "../tiptap/variable";
import { LINK_HTML_ATTRIBUTES } from "./link";

/**
 * Build and deduplicate TipTap editor extensions
 * Prevents duplicate extension warnings from TipTap
 */
export function buildEditorExtensions(placeholder: string): Extensions {
  const baseExtensions = [
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    }),
    TextStyle,
    Color,
    UnderlineMark,
    Highlight,
    Link.configure({
      autolink: true,
      linkOnPaste: true,
      // In an editor, clicking a link should place the cursor so the user can
      // manage it (edit/remove via the toolbar) — not navigate away.
      openOnClick: false,
      defaultProtocol: "https",
      HTMLAttributes: LINK_HTML_ATTRIBUTES,
    }),
    HorizontalRule,
    Placeholder.configure({
      placeholder,
      includeChildren: true,
    }),
    TextAlign.configure({
      types: ["heading", "paragraph", "image", "listItem"],
    }),
    Image.configure({
      inline: true,
      allowBase64: true,
      HTMLAttributes: {
        class: "max-w-full h-auto",
      },
    }),
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
    Variable.configure({ variablePattern: DEFAULT_VARIABLE_REGEX }),
  ];

  // Deduplicate by extension name to avoid TipTap warnings
  const seen = new Set<string>();
  return baseExtensions.filter((ext: AnyExtension) => {
    const name = ext.name;
    if (!name) return true;
    if (seen.has(name)) return false;
    seen.add(name);
    return true;
  });
}
